// Live-data preview for the 'multi-tab' capture template.
//
// Shows a horizontal tab strip across all records. The active tab
// renders that record's full form (header + detail grids). Same data
// pipeline as the other capture variants — useEmbedRecords +
// livePreviewProcess — so save / retrieve / discard / selection
// variable binding all match across templates.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertCircle, FilePlus, Loader2, RefreshCw, Save, Trash2,
} from 'lucide-react'

import { embedLoadModel, embedRunAction, applyActionResultViaOnChange, makeEmbedUploader } from '@/livepreview/embedDataApi'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import { useObjectVersion } from '@/store/objectVersionStore'

import { livePreviewDeploy } from '@/livepreview/api'
import {
  columnDbName, mainSqlModel, parseApiError,
} from '@/livepreview/utils'
import FormRenderer, { fieldAnchorId } from '@/livepreview/runtime/FormRenderer'
import {
  useEmbedRecords, type EmbedRecord,
} from '@/livepreview/useEmbedRecords'
import { useWarningConfirm } from '@twasta/page-runtime/txn'
import { useItemChange } from '@/livepreview/useItemChange'
import type { RecordStatus } from '@/livepreview/types'

import type {
  ColumnDef, SqlModel, TransactionModel,
} from '@/pages/designers/transaction/types'
import { GridPreviewView } from '@/pages/designers/transaction/PreviewView'
import type { EmbedConfig, PageInput } from './types'
import ErrorPanel from './embedTable/ErrorPanel'
import type { GridErrorItem } from './embedTable/gridTypes'

interface Props {
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  /** Site-mode routing for model + CRUD. */
  dataAccess?: DataAccess
  onSetInput?: (name: string, value: unknown) => void
}

const deployedTxns = new Map<string, Promise<void>>()
function deployKey(project: string, obj: string) { return `${project}::${obj}` }

async function ensureDeployed(project: string, obj: string): Promise<void> {
  const key = deployKey(project, obj)
  const existing = deployedTxns.get(key)
  if (existing) return existing
  const p = livePreviewDeploy(project, obj).then(() => undefined)
  deployedTxns.set(key, p)
  try {
    await p
  } catch (err) {
    deployedTxns.delete(key)
    throw err
  }
}

const STATUS_DOT: Record<RecordStatus, string> = {
  retrieved: '#64748b', dirty: '#eab308', unsaved: '#3b82f6',
  saved: '#16a34a', failed: '#ef4444', partial: '#f97316',
}

function SyncStatus({
  inSync, counts,
}: {
  inSync: boolean
  counts: Record<RecordStatus, number>
}) {
  if (inSync) {
    const total = counts.retrieved + counts.saved
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 10,
        fontSize: '0.8125rem', fontWeight: 600,
        background: 'rgba(16,185,129,0.18)', color: '#047857',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }} title="Local state matches the server">
        ● in sync{total > 0 ? ` · ${total}` : ''}
      </span>
    )
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 10,
      fontSize: '0.8125rem', fontWeight: 600,
      background: 'rgba(234,179,8,0.18)', color: '#a16207',
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }} title="Local edits have not been pushed to the server">
      ● not synced
    </span>
  )
}

export default function EmbedCaptureTabPreview({
  project, embed, inputs: _inputs, inputValues, onSetInput,
  dataAccess,
}: Props) {
  const objName = embed.obj_name
  const txnVersion = useObjectVersion('T', project, objName || '')

  const [model, setModel] = useState<TransactionModel | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [modelLoading, setModelLoading] = useState(false)

  useEffect(() => {
    if (!project || !objName) {
      setModel(null)
      setModelError(null)
      return
    }
    let cancelled = false
    setModelLoading(true)
    setModelError(null)
    embedLoadModel(dataAccess, project, objName)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        if (!content) {
          setModel(null)
          setModelError(`Transaction "${objName}" not yet generated`)
          return
        }
        const m = content.transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setModelError('Failed to load transaction')
      })
      .finally(() => { if (!cancelled) setModelLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, txnVersion, dataAccess])

  const sm = useMemo(() => (model ? mainSqlModel(model) : null), [model])
  const details = useMemo<SqlModel[]>(() => {
    if (!model) return []
    return (model.sql_models || []).slice(1)
      .map(w => w?.sql_model)
      .filter((x): x is SqlModel => !!x)
  }, [model])

  const [deployState, setDeployState] = useState<
    'idle' | 'deploying' | 'deployed' | 'error'
  >('idle')
  const [deployError, setDeployError] = useState<string | null>(null)

  // "Ignore warnings and proceed" confirm for business-logic warnings on save.
  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const records = useEmbedRecords({
    dataAccess,
    project, objName: objName || '', inputValues,
    filters: embed.filters || [], args: embed.args || [], recordVar: embed.recordVar,
    sm,
    confirmWarnings,
  })

  const filtersSig = useMemo(() => {
    const fs = embed.filters || []
    return JSON.stringify(fs.map((f) => {
      const ref = f.value
      let val: unknown
      if (ref?.source === 'page_input' && ref.name) val = inputValues[ref.name] ?? ''
      else if (ref) val = ref.value ?? ''
      let fromVal: unknown
      let toVal: unknown
      if (f.op === 'between') {
        if (f.from?.source === 'page_input' && f.from.name) fromVal = inputValues[f.from.name] ?? ''
        else if (f.from) fromVal = f.from.value ?? ''
        if (f.to?.source === 'page_input' && f.to.name) toVal = inputValues[f.to.name] ?? ''
        else if (f.to) toVal = f.to.value ?? ''
      }
      return { c: f.column, o: f.op, v: val, f: fromVal, t: toVal }
    }))
  }, [embed.filters, inputValues])

  const initialRetrieveDoneRef = useRef(false)
  useEffect(() => {
    if (!initialRetrieveDoneRef.current) {
      initialRetrieveDoneRef.current = true
      return
    }
    if (deployState !== 'deployed') return
    if (records.hasPendingChanges) return
    records.retrieve().catch(() => { /* surfaced via lastSaveError */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSig, deployState])

  useEffect(() => {
    if (!project || !objName) return
    let cancelled = false
    // Site mode (public end-user page): schema already deployed by the author;
    // site users must not call the author-only deploy endpoint (it 401s).
    if (dataAccess?.mode === 'site') {
      setDeployState('deployed')
      void records.retrieve().catch(() => { /* non-fatal */ })
      return () => { cancelled = true }
    }
    setDeployState('deploying')
    setDeployError(null)
    ensureDeployed(project, objName)
      .then(async () => {
        if (cancelled) return
        setDeployState('deployed')
        try { await records.retrieve() } catch { /* non-fatal */ }
      })
      .catch((err) => {
        if (cancelled) return
        const { message } = parseApiError(err)
        setDeployError(message)
        setDeployState('error')
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, dataAccess])

  const [activeIdx, setActiveIdx] = useState(0)
  // Tracks whether we've already attempted to restore the active tab
  // from the saved `selectedIdVar`. Restoration is a one-shot during
  // the initial records load — subsequent navigation is driven by
  // the user clicking tabs. Without this guard, the effect would
  // fight handleSelect every time inputValues mutates downstream.
  const restoredRef = useRef(false)
  useEffect(() => {
    if (records.records.length === 0) {
      setActiveIdx(0)
      restoredRef.current = false
      return
    }
    // First-load: try to land on the record whose primaryKey matches
    // the saved selection variable. Falls through to the clamp /
    // default-0 behaviour when no var is set or the saved row is
    // gone from the loaded set.
    if (!restoredRef.current) {
      restoredRef.current = true
      const varName = embed.selectedIdVar
      if (varName) {
        const raw = inputValues[varName]
        const targetPk = raw == null || raw === '' ? null : String(raw)
        if (targetPk) {
          const idx = records.records.findIndex((r) => r.primaryKey === targetPk)
          if (idx >= 0) {
            setActiveIdx(idx)
            // Seed lastSelectionRef so the writeSelectionVars effect
            // that fires on the resulting activeRec change treats
            // the echo as a no-op rather than overwriting the var.
            lastSelectionRef.current = { id: targetPk, data: records.records[idx] }
            return
          }
        }
      }
    }
    if (activeIdx >= records.records.length) {
      setActiveIdx(records.records.length - 1)
    }
  }, [records.records, activeIdx, embed.selectedIdVar, inputValues])

  const activeRec: EmbedRecord | null =
    records.records[activeIdx] ?? null

  const lastSelectionRef = useRef<{ id: string | null; data: unknown }>({
    id: null, data: null,
  })
  const writeSelectionVars = useCallback((rec: EmbedRecord | null) => {
    if (!onSetInput) return
    const pk = rec?.primaryKey ?? null
    const last = lastSelectionRef.current
    if (embed.selectedIdVar && last.id !== pk) {
      onSetInput(embed.selectedIdVar, pk ?? '')
    }
    if (embed.selectedDataVar && last.data !== rec) {
      onSetInput(embed.selectedDataVar, rec ? rec.header : undefined)
    }
    lastSelectionRef.current = { id: pk, data: rec }
  }, [embed.selectedIdVar, embed.selectedDataVar, onSetInput])

  useEffect(() => {
    writeSelectionVars(activeRec)
  }, [activeRec, writeSelectionVars])

  const triggerItemChange = useItemChange<{ recordId: string }>({
    dataAccess,
    project, objName: objName || '', sm,
    onUpdates: (ctx, updates) => {
      records.updateHeader(ctx.recordId, updates)
    },
  })

  const onFieldChange = useCallback((name: string, v: unknown) => {
    if (!activeRec) return
    const newHeader = { ...activeRec.header, [name]: v }
    records.updateHeader(activeRec.id, { [name]: v })
    triggerItemChange({ recordId: activeRec.id }, name, newHeader)
    writeSelectionVars({ ...activeRec, header: newHeader })
  }, [activeRec, records, triggerItemChange, writeSelectionVars])

  const handleAdd = useCallback(async () => {
    try {
      const rec = await records.addNew()
      queueMicrotask(() => {
        setActiveIdx((prev) => prev + 1)
        writeSelectionVars(rec)
      })
    } catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Could not add new record: ${message}`)
    }
  }, [records, writeSelectionVars])

  const handleSaveAll = useCallback(async () => {
    const outcome = await records.saveBatch()
    if (outcome.success) {
      toast.success('All records saved')
    } else if (outcome.failedCount > 0) {
      toast.error(`${outcome.failedCount} record${outcome.failedCount === 1 ? '' : 's'} failed validation`)
    }
  }, [records])

  const handleRetrieve = useCallback(async () => {
    if (records.hasPendingChanges) return
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Refresh failed: ${message}`)
    }
  }, [records])

  const handleDiscardChanges = useCallback(async () => {
    const pending = records.counts.unsaved + records.counts.dirty +
      records.counts.failed + records.counts.partial
    if (!window.confirm(
      `Discard ${pending} pending change${pending === 1 ? '' : 's'} and reload from the server?`,
    )) return
    records.discardLocalEdits()
    try { await records.retrieve() }
    catch (err) {
      const { message } = parseApiError(err)
      toast.error(`Refresh failed: ${message}`)
    }
  }, [records])

  const handleRemoveActive = useCallback(() => {
    if (!activeRec) return
    const msg = activeRec.status === 'unsaved'
      ? 'Remove this unsaved record?'
      : `Drop "${activeRec.primaryKey || ''}" from the local edit set (server copy stays)?`
    if (!window.confirm(msg)) return
    records.removeLocal(activeRec.id)
    setActiveIdx((i) => Math.max(0, i - 1))
  }, [activeRec, records])

  // ── Error side panel (aggregates every field + record error across
  // all tabs, since the user can only see one record at a time and
  // needs to know what's failing on the others).
  // NOTE: these hooks MUST stay ABOVE the early returns below — `sm`
  // resolves from null → defined as the model loads, so a conditional
  // hook here would change the hook count between renders (React #310). ──
  const errorItems: GridErrorItem[] = useMemo(() => {
    const items: GridErrorItem[] = []
    records.records.forEach((rec, idx) => {
      const rowLabel = rec.primaryKey
        ? `Tab — ${String(rec.primaryKey)}`
        : `Tab — New ${idx + 1}`
      if (rec.recordError) {
        items.push({
          rowKey: rec.id, rowLabel,
          column: null, columnLabel: null,
          message: rec.recordError,
          section: `Record ${idx + 1}`,
        })
      }
      for (const [field, msg] of Object.entries(rec.fieldErrors || {})) {
        items.push({
          rowKey: rec.id,
          rowLabel,
          column: field,
          columnLabel: columnLabelFromSm(sm, field),
          message: msg,
          section: `Record ${idx + 1}`,
        })
      }
    })
    return items
  }, [records.records, sm])

  const [errorPanelOpen, setErrorPanelOpen] = useState(false)
  const errorCount = errorItems.length
  useEffect(() => {
    if (errorCount > 0 && records.lastSaveError) setErrorPanelOpen(true)
    if (errorCount === 0) setErrorPanelOpen(false)
  }, [errorCount, records.lastSaveError])

  const handleErrorSelect = useCallback((item: GridErrorItem) => {
    // Switch to the record's tab.
    const idx = records.records.findIndex(r => r.id === item.rowKey)
    if (idx >= 0) setActiveIdx(idx)
    if (item.column) {
      // Defer so the active record's form renders before we focus.
      requestAnimationFrame(() => {
        const el = document.getElementById(fieldAnchorId(item.column!))
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const input = el.querySelector<HTMLElement>(
          'input, select, textarea, button',
        )
        if (input) input.focus()
      })
    }
  }, [records.records])

  if (!objName) {
    return (
      <div style={emptyStyle}>
        <AlertCircle size={18} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }
  if (modelLoading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction model…</span>
      </div>
    )
  }
  if (modelError) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{modelError}</span>
      </div>
    )
  }
  if (!sm) return null

  const pending = records.counts.unsaved + records.counts.dirty +
    records.counts.failed + records.counts.partial

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginRight: 'auto' }}>
          <SyncStatus inSync={!records.hasPendingChanges} counts={records.counts} />
          {deployState === 'deploying' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
              deploying…
            </span>
          )}
          {deployState === 'error' && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-error, #b91c1c)' }}>
              deploy failed: {deployError}
            </span>
          )}
        </div>
        {errorCount > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setErrorPanelOpen(true)}
            style={{ color: '#b91c1c' }}
            title="Show all validation errors across tabs"
          >
            <AlertCircle size={14} /> {errorCount} error{errorCount === 1 ? '' : 's'}
          </button>
        )}
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleRetrieve}
          disabled={records.busy || deployState !== 'deployed' || records.hasPendingChanges}
          title={
            records.hasPendingChanges
              ? 'Local changes are not synced — Save or Discard them before refreshing'
              : 'Reload from the server'
          }
        ><RefreshCw size={14} /> Refresh</button>
        {records.hasPendingChanges && (
          <button
            type="button" className="btn btn-ghost btn-sm"
            onClick={handleDiscardChanges}
            disabled={records.busy || deployState !== 'deployed'}
            title="Drop pending changes and reload"
            style={{ color: '#b91c1c' }}
          ><Trash2 size={14} /> Discard changes</button>
        )}
        <button
          type="button" className="btn btn-ghost btn-sm"
          onClick={handleAdd}
          disabled={records.busy || deployState !== 'deployed'}
        ><FilePlus size={14} /> Add tab</button>
        <button
          type="button" className="btn btn-primary btn-sm"
          onClick={handleSaveAll}
          disabled={records.busy || deployState !== 'deployed' || pending === 0}
        >
          <Save size={14} /> {embed.captureSaveLabel || 'Save All'}
          {pending > 0 && (
            <span style={{ marginLeft: 4, opacity: 0.85 }}>({pending})</span>
          )}
        </button>
      </div>

      {records.hasPendingChanges && (
        <div style={{
          padding: '6px 10px', fontSize: '0.8425rem',
          background: 'rgba(234,179,8,0.10)',
          border: '1px solid rgba(234,179,8,0.40)',
          borderRadius: 6, color: '#a16207',
        }}>
          Local changes are not synced. Refresh is disabled until you{' '}
          <strong>{embed.captureSaveLabel || 'Save'}</strong> or{' '}
          <strong>Discard changes</strong>.
        </div>
      )}

      {records.lastSaveError && (
        <div style={{
          padding: '6px 10px',
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 6, color: '#b91c1c', fontSize: '0.8425rem',
        }}>{records.lastSaveError}</div>
      )}

      {/* Tab strip + active form */}
      {records.records.length > 0 ? (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 8,
          background: 'var(--color-bg)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', gap: 4, flexWrap: 'wrap',
            padding: '6px 8px 0', alignItems: 'flex-end',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            {records.records.map((rec, i) => {
              const isActive = i === activeIdx
              const dot = STATUS_DOT[rec.status]
              const label = rec.primaryKey
                ? `${rec.primaryKey.slice(0, 12)}${rec.primaryKey.length > 12 ? '…' : ''}`
                : `New ${i + 1}`
              return (
                <button
                  key={rec.id}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 12px', fontSize: '0.8425rem',
                    border: '1px solid var(--color-border)',
                    borderBottom: isActive ? '1px solid var(--color-bg)' : '1px solid var(--color-border)',
                    borderRadius: '6px 6px 0 0',
                    background: isActive ? 'var(--color-bg)' : 'transparent',
                    color: 'var(--color-text)', cursor: 'pointer',
                    marginBottom: -1, fontWeight: isActive ? 600 : 400,
                  }}
                  title={`${rec.status}${rec.primaryKey ? ` · ${rec.primaryKey}` : ''}`}
                >
                  <span style={{
                    display: 'inline-block', width: 8, height: 8,
                    borderRadius: '50%', background: dot,
                  }} />
                  {label}
                </button>
              )
            })}
          </div>
          {activeRec && (
            <RecordBody
              rec={activeRec}
              sm={sm}
              details={details}
              project={project}
              objName={objName}
              columnLabel={(dbName) => columnLabelFromSm(sm, dbName)}
              onFieldChange={onFieldChange}
              onRemove={handleRemoveActive}
              uploadDocument={makeEmbedUploader(dataAccess, project)}
              dataAccess={dataAccess}
            />
          )}
        </div>
      ) : (
        <div style={{
          padding: '24px 16px', textAlign: 'center',
          color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)', borderRadius: 8,
        }}>
          No records yet. Click <strong>Add tab</strong> to start a new
          record, or <strong>Refresh</strong> to load existing records.
        </div>
      )}

      <ErrorPanel
        open={errorPanelOpen}
        errors={errorItems}
        onClose={() => setErrorPanelOpen(false)}
        onSelect={handleErrorSelect}
        title="Save errors across tabs"
      />
      {/* "Ignore warnings and proceed" confirm for save warnings. */}
      {warningsModal}
    </div>
  )
}

function RecordBody({
  rec, sm, details, project, objName, columnLabel,
  onFieldChange, onRemove, uploadDocument, dataAccess,
}: {
  rec: EmbedRecord
  sm: SqlModel
  details: SqlModel[]
  project: string
  objName: string
  columnLabel: (dbName: string) => string
  onFieldChange: (name: string, v: unknown) => void
  onRemove: () => void
  uploadDocument: (file: File) => Promise<{ url: string; name?: string }>
  dataAccess?: DataAccess
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {(rec.recordError || Object.keys(rec.fieldErrors).length > 0) && (
        <ValidationBanner
          fieldErrors={rec.fieldErrors}
          banner={rec.recordError}
          columnLabel={columnLabel}
        />
      )}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '6px 14px',
      }}>
        <button
          type="button"
          onClick={onRemove}
          className="btn btn-ghost btn-xs"
          title="Remove this record from the local edit set"
          style={{ color: '#b91c1c' }}
        ><Trash2 size={12} /> Remove tab</button>
      </div>
      <div style={{ padding: '0 14px 14px' }}>
        <FormRenderer
          model={sm}
          values={rec.header}
          errors={rec.fieldErrors}
          onChange={onFieldChange}
          readOnlyKey={!!rec.primaryKey}
          project={project}
          objName={objName}
          uploadDocument={uploadDocument}
          runAction={(p, o, req) => embedRunAction(dataAccess, p, o, req)}
          formNo={sm.form_no}
          txnId={rec.primaryKey || undefined}
          onActionResult={(res) => applyActionResultViaOnChange(res, sm.form_no || '1', onFieldChange)}
          onActionError={(err) => toast.error(parseApiError(err).message)}
        />
      </div>
      {details.length > 0 && (
        <div style={{
          padding: 14, display: 'flex', flexDirection: 'column', gap: 10,
          borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
        }}>
          {details.map((d, di) => (
            <div key={di} style={{
              border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'var(--color-bg)', overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '4px 10px', fontSize: '0.8125rem',
                background: 'var(--color-bg-secondary)',
                borderBottom: '1px solid var(--color-border)',
              }}>
                <span style={{ fontWeight: 600 }}>
                  {d.form_title || `Detail ${di + 1}`}
                </span>
                <span style={{
                  padding: '1px 6px', borderRadius: 8, fontSize: '0.8125rem',
                  background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
                }}>Detail grid</span>
              </div>
              <GridPreviewView form={d} sampleRows={2} />
            </div>
          ))}
          <div style={{
            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            fontStyle: 'italic',
          }}>
            Detail-row editing is not yet wired in the designer preview;
            the runtime captures detail rows for this record.
          </div>
        </div>
      )}
    </div>
  )
}

function ValidationBanner({
  fieldErrors, banner, columnLabel,
}: {
  fieldErrors: Record<string, string>
  banner: string | null
  columnLabel: (dbName: string) => string
}) {
  const keys = Object.keys(fieldErrors)
  if (keys.length === 0 && !banner) return null
  return (
    <div style={{
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.08)',
      borderBottom: '1px solid rgba(239,68,68,0.35)',
      color: '#ef4444', fontSize: '0.8425rem',
    }}>
      <div style={{ fontWeight: 600, marginBottom: keys.length ? 4 : 0 }}>
        {keys.length > 0
          ? `Save blocked — ${keys.length} validation error${keys.length === 1 ? '' : 's'}`
          : banner}
      </div>
      {keys.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {keys.map((k) => (
            <li key={k}>
              <strong>{columnLabel(k)}</strong>: {fieldErrors[k]}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function columnLabelFromSm(sm: SqlModel | null, dbName: string): string {
  if (!sm?.columns) return dbName
  for (const wrap of sm.columns) {
    const col = wrap.column as ColumnDef | undefined
    if (!col) continue
    const dbn = columnDbName(col)
    if (dbn === dbName) return col.heading || col.name || dbName
  }
  return dbName
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)', borderRadius: 8,
  color: 'var(--color-text-secondary)', fontSize: '0.9125rem',
}
